<template>
  <section class="payment-form">
    <div
      :class="[
        'payment-form__container',
        isBillingAddressFormShown && 'payment-form__container--billing',
      ]"
    >
      <div class="payment-form__title-wrapper">
        <h4 class="payment-form__title">{{ paymentFormTitle }}</h4>
        <button @click="handleCloseForm" class="btn btn--tertiary btn--small btn--training">
          <CloseModalXIcon />
        </button>
      </div>
      <div class="payment-form__form-wrapper">
        <form @submit.prevent="processCCEntries" ref="checkoutForm" class="cart-form">
          <PaymentFormCreditCard
            v-show="paymentMethod === 'card'"
            v-bind="{
              fields,
              errors,
              fieldHasError,
              clearError,
              cardNumber,
              cardMonth,
              cardYear,
              cardCVV,
              showCVV,
            }"
            @update-field="updateField"
          />
          <PaymentFormBillingAddress
            v-bind="{
              fields,
              errors,
              fieldHasError,
              clearError,
              paymentMethod,
            }"
            @update-field="updateField"
            @back-to-card="isBillingAddressFormShown = false"
          />
          <PaymentFormButton v-bind="{ errorsLength, paymentMethod }" />
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { validateForm, clearFormErrors } from '@/helpers'
import PaymentFormCreditCard from '@/components/PaymentFormCreditCard.vue'
import PaymentFormBillingAddress from '@/components/PaymentFormBillingAddress.vue'
import PaymentFormButton from '@/components/PaymentFormButton.vue'
import CloseModalXIcon from '@/assets/icons/close_modal_x.svg'
import { eventsConstants } from '@/helpers/eventsList'
import { mixpanelEvent, priceDetailUpdates } from '@/helpers/mixins'

export default {
  name: 'PaymentForm',
  components: {
    PaymentFormCreditCard,
    PaymentFormBillingAddress,
    PaymentFormButton,
    CloseModalXIcon: { ...CloseModalXIcon, name: 'CloseModalXIcon' },
  },
  mixins: [mixpanelEvent, priceDetailUpdates],
  data() {
    return {
      errors: clearFormErrors(),
      elements: '',
      paypal: '',
      fields: {
        'first name': {
          type: 'text',
          placeholder: 'First Name',
          rules: ['presence'],
          'data-recurly': 'first_name',
          value: '',
          autocomplete: 'given-name',
        },
        'last name': {
          type: 'text',
          placeholder: 'Last Name',
          'data-recurly': 'last_name',
          rules: ['presence'],
          value: '',
          autocomplete: 'family-name',
        },
        'street address': {
          type: 'text',
          rules: ['presence'],
          placeholder: 'Street Address',
          'data-recurly': 'address1',
          value: '',
          autocomplete: 'street-address',
        },
        country: {
          rules: ['presence'],
          'data-recurly': 'country',
          value: '',
          autocomplete: 'country',
        },
        city: {
          type: 'text',
          rules: ['presence'],
          placeholder: 'City',
          'data-recurly': 'city',
          value: '',
          autocomplete: 'locality',
        },
        state: {
          type: 'text',
          rules: ['presence'],
          placeholder: 'State/Province',
          'data-recurly': 'state',
          value: '',
          autocomplete: 'region',
        },
        zip: {
          type: 'text',
          rules: ['presence'],
          placeholder: 'Zip/Postal Code',
          'data-recurly': 'postal_code',
          value: '',
          autocomplete: 'postal-code',
        },
        phone: {
          type: 'tel',
          rules: ['presence'],
          placeholder: 'Phone Number',
          'data-recurly': 'phone',
          value: '',
          autocomplete: 'tel',
        },
        company: {
          type: 'text',
          rules: [],
          placeholder: 'Company',
          'data-recurly': 'company',
          value: '',
          autocomplete: 'organization',
        },
      },
      cardNumber: {},
      cardYear: {},
      cardMonth: {},
      cardCVV: {},
      isBillingAddressFormShown: false,
      showCVV: false,
    }
  },
  computed: {
    ...mapState({
      customCoupon: state => state.shoppingCart.coupon,
      paymentEntries: state => state.subscriptions.paymentEntries,
      paymentMethod: state => state.subscriptions.paymentMethod,
      recurlyToken: state => state.subscriptions.recurlyToken,
      profile: state => state.auth.userProfile,
      isLoading: state => state.app.isComponentLoading,
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    errorsLength() {
      return Object.keys(this.errors).length
    },
    streamingLink() {
      return `${process.env.VUE_APP_STREAMING_URL}`
    },
    redirectUrl() {
      return process.env.VUE_APP_REDIRECT_URL
    },
    paymentFormTitle() {
      return this.paymentMethod === 'card' ? 'Add Card' : 'Connect to PayPal'
    },
  },
  methods: {
    ...mapMutations({
      setIsPaymentModalShown: 'app/IS_PAYMENT_MODAL_SHOWN_UPDATED',
    }),
    updateField(evt) {
      const { name, input } = evt
      this.fields[name].value = input
    },
    handlePaypalErrors(error) {
      if (error?.message) {
        return this.$store.dispatch('app/setError', error.message)
      }
      return this.$store.dispatch(
        'app/setError',
        'Something went wrong with PayPal. Please try again.'
      )
    },
    handleRecurlyToken(token) {
      this.$store.dispatch('subscriptions/setRecurlyToken', token.id)
      if (this.isAuthenticated) {
        this.$store.dispatch('subscriptions/removePaymentMethodOnFile')
      }
      if (this.paymentMethod === 'paypal') {
        this.setIsPaymentModalShown(false)
        this.$router.push({ name: 'confirmation' })
      }
    },
    createPaypal() {
      this.paypal = window.recurly.PayPal({
        display: { displayName: ' INE ' },
      })
      this.paypal.on('error', this.handlePaypalErrors)
      this.paypal.on('token', this.handleRecurlyToken)
    },
    processPayPal() {
      if (!this.paypal && !this.paypal.start) {
        this.createPaypal()
      }
      this.paypal.start()
    },
    fieldHasError(field) {
      return !!(this.errors[field] && this.errors[field].length)
    },
    cardNumberChangeHandler(evt) {
      this.cardNumber = evt
      const { lastFour, brand } = evt
      this.$store.dispatch('subscriptions/updatePaymentEntries', {
        value: 'last4DigitsOfCreditCard',
        input: lastFour,
      })
      this.$store.dispatch('subscriptions/updatePaymentEntries', {
        value: 'creditCardVendor',
        input: brand,
      })
    },
    cardMonthChangeHandler(evt) {
      this.cardMonth = evt
    },
    cardYearChangeHandler(evt) {
      this.cardYear = evt
    },
    cardCVVChangeHandler(evt) {
      this.cardCVV = evt
    },
    cardCVVShowCVVTemplate() {
      this.showCVV = true
    },
    cardCVVHideCVVTemplate() {
      this.showCVV = false
    },
    attachRecurlyElements() {
      const elementStyles = {
        fontColor: 'white',
        fontSize: '12px',
        placeholder: {
          color: '#ACB2BF !important',
        },
      }
      this.elements
        .CardNumberElement({
          format: true,
          inputType: 'text',
          style: { ...elementStyles, placeholder: { content: 'Card Number' } },
        })
        .on('change', this.cardNumberChangeHandler)
        .on('focus', this.cardCVVHideCVVTemplate)
        .attach('#card-number')
      this.elements
        .CardMonthElement({
          format: true,
          inputType: 'text',
          style: { ...elementStyles, placeholder: { content: 'MM' } },
        })
        .on('change', this.cardMonthChangeHandler)
        .on('focus', this.cardCVVHideCVVTemplate)
        .attach('#card-month')
      this.elements
        .CardYearElement({
          inputType: 'text',
          style: { ...elementStyles, placeholder: { content: 'YYYY' } },
        })
        .on('change', this.cardYearChangeHandler)
        .on('focus', this.cardCVVHideCVVTemplate)
        .attach('#card-year')
      this.elements
        .CardCvvElement({
          inputType: 'text',
          style: { ...elementStyles, placeholder: { content: 'CVV' } },
        })
        .on('change', this.cardCVVChangeHandler)
        .on('focus', this.cardCVVShowCVVTemplate)
        .attach('#card-cvv')
    },
    async handleFormSubmit() {
      let fields = this.fields
      if (this.paymentMethod === 'paypal') {
        fields = Object.entries(fields).reduce((acc, [key, value]) => {
          if (key !== 'first name' && key !== 'last name') {
            acc[key] = value
          }
          return acc
        }, {})
      }
      const { isValid, errors } = validateForm(fields)
      if (isValid) {
        this.gaAddPaymentInfo()
        this.submitForm()
      } else {
        this.errors = errors
        this.$store.dispatch(
          'app/setError',
          `These credit card fields appear to be invalid: ${Object.keys(errors).join(', ')}`
        )
      }
    },
    async processCCEntries() {
      if (this.paymentMethod === 'paypal') {
        this.handleFormSubmit()
      } else {
        window.recurly.token(this.elements, this.$refs.checkoutForm, (err, token) => {
          if (err) {
            this.$store.dispatch('app/setIsComponentLoading', false)
            if (err.fields.some(field => ['number', 'month', 'year'].includes(field))) {
              this.$store.dispatch(
                'app/setError',
                `These credit card fields appear to be invalid: ${err.fields.join(', ')}`
              )
            } else {
              const { errors } = validateForm(this.fields)
              this.errors = errors
              this.$store.dispatch(
                'app/setError',
                `These credit card fields appear to be invalid: ${Object.keys(this.errors).join(
                  ', '
                )}`
              )
            }
          } else {
            this.handleRecurlyToken(token)
            this.handleFormSubmit()
          }
        })
      }
    },
    submitForm() {
      if (this.recurlyToken && this.recurlyToken.length) {
        this.$store.dispatch('events/sendBrazeEventAction', {
          event_name: eventsConstants.CHECKOUT_CART_UPDATED,
          properties: {
            trigger: 'recurlyTokenConfirmation',
          },
        })
        this.setIsPaymentModalShown(false)
      }
      this.$store.dispatch('app/setIsComponentLoading', true)
      if (this.paymentMethod === 'card') {
        this.$store.dispatch('events/sendBrazeEventAction', {
          event_name: eventsConstants.CHECKOUT_CART_UPDATED,
          properties: {
            trigger: 'processCreditCard',
          },
        })
        this.$store.dispatch('app/setIsComponentLoading', false)
        return this.$router.push({ name: 'confirmation' })
      }
      this.processPayPal()
      this.$store.dispatch('events/sendBrazeEventAction', {
        event_name: eventsConstants.CHECKOUT_CART_UPDATED,
        properties: {
          trigger: 'processPayPal',
        },
      })
      this.$store.dispatch('app/setIsComponentLoading', false)
    },
    startRecurly() {
      this.$store.dispatch('subscriptions/setRecurlyToken', '')
      this.elements = window.recurly.Elements()
      this.attachRecurlyElements()
    },
    clearError(field) {
      this.errors = Object.entries(this.errors).reduce((acc, [key, val], _) => {
        if (key !== field) {
          acc[key] = val
        }
        return acc
      }, {})
    },
    clearErrors() {
      this.errors = clearFormErrors()
    },
    hydrateValues() {
      const { email, first_name, last_name } = this.profile
      this.$store.dispatch('subscriptions/updatePaymentEntries', {
        value: 'email',
        input: email,
      })
      this.$store.dispatch('subscriptions/updatePaymentEntries', {
        value: 'firstName',
        input: first_name,
      })
      this.$store.dispatch('subscriptions/updatePaymentEntries', {
        value: 'lastName',
        input: last_name,
      })
      const {
        paymentEntries: {
          country,
          firstName,
          lastName,
          streetAddress,
          city,
          state,
          zip,
          phone,
          company,
        },
      } = this
      this.fields.country.value = country
      this.fields['first name'].value = firstName
      this.fields['last name'].value = lastName
      this.fields['street address'].value = streetAddress
      this.fields.city.value = city
      this.fields.state.value = state
      this.fields.zip.value = zip
      this.fields.phone.value = phone
      this.fields.company.value = company
    },
    startPaymentCollectionModules() {
      this.$store.dispatch('subscriptions/setRecurlyToken', '')
      this.startRecurly()
      this.createPaypal()
    },
    handleCloseForm() {
      this.$store.dispatch('subscriptions/setRecurlyToken', '')
      this.setIsPaymentModalShown(false)
    },
  },
  created() {
    if (this.coupon.length > 0) {
      this.addCoupon()
    }
    this.hydrateValues()
  },
  mounted() {
    if (!this.recurlyToken) {
      this.startPaymentCollectionModules()
    }
  },
}
</script>

<style lang="scss" scoped>
.payment-form {
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  &__container {
    background: var(--black-2);
    width: 100vw;
    height: 100vh;
    display: flex;
    flex-wrap: wrap;
    flex-flow: column;
    justify-content: flex-start;
    padding: 25px;
    @include tablet {
      justify-content: center;
      border-radius: 35px;
      width: 550px;
      height: auto;
      padding: 40px;
    }
    &--billing {
      @include tablet {
        height: auto;
      }
    }
  }
  &__title {
    color: var(--white);
    font-family: var(--font-regular);
    font-style: normal;
    margin: 0;
    font-weight: 700;
    font-size: 24px;
    line-height: 32px;
    &-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }
  }
  &__btn-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    margin: auto 0 0 0;
    flex-wrap: wrap;
    position: absolute;
    bottom: 30px;
    right: 3%;
    @include mobile {
      right: 25px;
    }
    @include tablet {
      height: auto;
      position: relative;
      bottom: unset;
      right: unset;
    }
  }
  .cart-form {
    &__secondary {
      &__subtitle {
        font-size: 1.3rem;
      }
      margin: 0 0 auto 0;
    }
  }
  &__form-wrapper {
    width: 100%;
  }
}
</style>
